import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

// API URL (環境変数から取得)
const API_URL = import.meta.env.VITE_API_URL || 'https://ninshousystem-api-361153976392.asia-northeast1.run.app';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// コピーボタン付きのコードブロック
function CodeBlock({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ position: 'relative', my: 2 }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {label}
        </Typography>
      )}
      <Paper
        sx={{
          p: 2,
          bgcolor: 'grey.900',
          color: 'grey.100',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleCopy}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'grey.400',
            '&:hover': { color: 'grey.100' },
          }}
        >
          {copied ? <CheckIcon color="success" /> : <CopyIcon />}
        </IconButton>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {code}
        </pre>
      </Paper>
    </Box>
  );
}

// URLフィールド
function WebhookUrlField({ provider }: { provider: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${API_URL}/webhook/gateway?provider=${provider}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <TextField
      fullWidth
      value={url}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={copied ? 'コピーしました!' : 'コピー'}>
              <IconButton onClick={handleCopy} edge="end">
                {copied ? <CheckIcon color="success" /> : <CopyIcon />}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
        sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
      }}
      sx={{ my: 2 }}
    />
  );
}

export const WebhookSetupGuidePage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setActiveStep(0);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Webhook設定ガイド
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        各決済プロバイダーからの購入通知を受け取るための設定手順です。
      </Typography>

      {/* 概要カード */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <InfoIcon color="primary" />
            <Typography variant="h6">仕組み</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ユニバーサルWebhookゲートウェイは、複数の決済プロバイダーからの通知を統一的に処理します。
            購入が完了すると、自動的にユーザー作成・ライセンス発行・メール送信が行われます。
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="コネワン" color="primary" variant="outlined" />
            <Chip label="Stripe" color="secondary" variant="outlined" />
            <Chip label="自社マーケットプレイス" variant="outlined" />
            <Chip label="Gumroad" variant="outlined" disabled />
            <Chip label="Paddle" variant="outlined" disabled />
          </Box>
        </CardContent>
      </Card>

      {/* プロバイダー選択タブ */}
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="コネワン" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Stripe" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="自社マーケットプレイス" icon={<CodeIcon />} iconPosition="start" />
        </Tabs>

        {/* コネワン設定 */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              コネワンは日本向けのサブスクリプション決済サービスです。
              コネワン管理画面でWebhook URLを設定する必要があります。
            </Alert>

            <Typography variant="h6" gutterBottom>
              Webhook URL
            </Typography>
            <WebhookUrlField provider="koneone" />

            <Stepper activeStep={activeStep} orientation="vertical">
              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">コネワン管理画面にログイン</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    コネワンの管理画面にログインしてください。
                  </Typography>
                  <Button
                    variant="outlined"
                    endIcon={<OpenInNewIcon />}
                    href="https://koneone.jp/login"
                    target="_blank"
                    sx={{ mt: 2 }}
                  >
                    コネワン管理画面を開く
                  </Button>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(1)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">Webhook設定画面に移動</Typography>
                </StepLabel>
                <StepContent>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="左メニューから「設定」をクリック" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="「Webhook」タブを選択" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="「更新された注文」セクションを探す" />
                    </ListItem>
                  </List>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(0)} sx={{ mr: 1 }}>戻る</Button>
                    <Button onClick={() => setActiveStep(2)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">Webhook URLを設定</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    「更新された注文」のWebhook URLフィールドに以下を入力してください：
                  </Typography>
                  <WebhookUrlField provider="koneone" />
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(1)} sx={{ mr: 1 }}>戻る</Button>
                    <Button onClick={() => setActiveStep(3)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">シークレットキーを設定（オプション）</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    セキュリティ強化のため、シークレットキーを設定することを推奨します。
                    コネワンで設定したシークレットキーを、サーバーの環境変数 <code>WEBHOOK_SECRET</code> にも設定してください。
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    本番環境では必ずシークレットキーを設定してください。
                  </Alert>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(2)} sx={{ mr: 1 }}>戻る</Button>
                    <Button onClick={() => setActiveStep(4)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">プロダクトのwebhook_configを設定</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    「プロダクト管理」画面で、対象プロダクトの webhook_config を設定します。
                    コネワンの商品ID・バリアントIDと内部のプランコードをマッピングします。
                  </Typography>
                  <CodeBlock
                    label="webhook_config 設定例"
                    code={`{
  "koneone": {
    "enabled": true,
    "product_ids": ["あなたのコネワン商品ID"],
    "variant_mappings": {
      "コネワンのBasicバリアントID": "basic",
      "コネワンのProバリアントID": "pro",
      "コネワンのEnterpriseバリアントID": "enterprise"
    }
  }
}`}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => window.location.href = '/admin/products'}
                    sx={{ mt: 2 }}
                  >
                    プロダクト管理画面へ
                  </Button>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(3)} sx={{ mr: 1 }}>戻る</Button>
                    <Button variant="contained" onClick={() => setActiveStep(5)}>完了</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">設定完了!</Typography>
                </StepLabel>
                <StepContent>
                  <Alert severity="success">
                    コネワンのWebhook設定が完了しました。テスト購入を行って動作を確認してください。
                  </Alert>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(0)}>最初から確認</Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        </TabPanel>

        {/* Stripe設定 */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Stripeはグローバルな決済プラットフォームです。
              あなたのStripeアカウントで直接決済を処理する場合に使用します。
            </Alert>

            <Typography variant="h6" gutterBottom>
              Webhook URL
            </Typography>
            <WebhookUrlField provider="stripe" />

            <Stepper activeStep={activeStep} orientation="vertical">
              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">Stripe Dashboardにログイン</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    Stripeの管理画面にログインしてください。
                  </Typography>
                  <Button
                    variant="outlined"
                    endIcon={<OpenInNewIcon />}
                    href="https://dashboard.stripe.com"
                    target="_blank"
                    sx={{ mt: 2 }}
                  >
                    Stripe Dashboardを開く
                  </Button>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(1)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">Webhooks設定画面に移動</Typography>
                </StepLabel>
                <StepContent>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="左メニュー「Developers」→「Webhooks」をクリック" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="「+ Add endpoint」ボタンをクリック" />
                    </ListItem>
                  </List>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(0)} sx={{ mr: 1 }}>戻る</Button>
                    <Button onClick={() => setActiveStep(2)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">エンドポイントURLを設定</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    「Endpoint URL」フィールドに以下を入力してください：
                  </Typography>
                  <WebhookUrlField provider="stripe" />
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(1)} sx={{ mr: 1 }}>戻る</Button>
                    <Button onClick={() => setActiveStep(3)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">イベントを選択</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    以下のイベントを選択してください：
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                      <ListItemText
                        primary="checkout.session.completed"
                        secondary="チェックアウト完了時（初回購入）"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                      <ListItemText
                        primary="invoice.paid"
                        secondary="請求書支払い完了時（サブスク更新）"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                      <ListItemText
                        primary="customer.subscription.deleted"
                        secondary="サブスクリプション削除時"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="warning" /></ListItemIcon>
                      <ListItemText
                        primary="charge.refunded"
                        secondary="返金時（オプション）"
                      />
                    </ListItem>
                  </List>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(2)} sx={{ mr: 1 }}>戻る</Button>
                    <Button onClick={() => setActiveStep(4)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">Signing Secretを取得</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    エンドポイント作成後、「Signing secret」を表示してコピーします。
                    このシークレットをサーバーの環境変数 <code>STRIPE_WEBHOOK_SECRET</code> に設定してください。
                  </Typography>
                  <CodeBlock
                    label=".env設定例"
                    code="STRIPE_WEBHOOK_SECRET=whsec_xxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(3)} sx={{ mr: 1 }}>戻る</Button>
                    <Button onClick={() => setActiveStep(5)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">プロダクトのwebhook_configを設定</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    StripeのProduct IDとPrice IDをマッピングします。
                  </Typography>
                  <CodeBlock
                    label="webhook_config 設定例"
                    code={`{
  "stripe": {
    "enabled": true,
    "product_ids": ["prod_xxxxxx"],
    "plan_mappings": {
      "price_basic_monthly": "basic",
      "price_pro_monthly": "pro",
      "price_enterprise_monthly": "enterprise"
    }
  }
}`}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(4)} sx={{ mr: 1 }}>戻る</Button>
                    <Button variant="contained" onClick={() => setActiveStep(6)}>完了</Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">設定完了!</Typography>
                </StepLabel>
                <StepContent>
                  <Alert severity="success">
                    StripeのWebhook設定が完了しました。Stripeのテストモードで動作を確認してください。
                  </Alert>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(0)}>最初から確認</Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        </TabPanel>

        {/* 自社マーケットプレイス設定 */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              自社マーケットプレイスでは、購入完了時にAPIを直接呼び出します。
              内部のプロダクトコード・プランコードを直接指定できます。
            </Alert>

            <Typography variant="h6" gutterBottom>
              Webhook URL
            </Typography>
            <WebhookUrlField provider="gambit_marketplace" />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              API呼び出し例
            </Typography>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
              1. 購入完了時
            </Typography>
            <CodeBlock
              code={`curl -X POST "${API_URL}/webhook/gateway?provider=gambit_marketplace" \\
  -H "Content-Type: application/json" \\
  -H "X-Webhook-Signature: \${SIGNATURE}" \\
  -d '{
    "event": "purchase",
    "email": "customer@example.com",
    "product_code": "gambit",
    "plan_code": "pro",
    "customer_name": "山田太郎",
    "order_id": "order_12345",
    "expires_at": "2025-12-08T00:00:00Z"
  }'`}
            />

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
              2. サブスクリプション更新時
            </Typography>
            <CodeBlock
              code={`curl -X POST "${API_URL}/webhook/gateway?provider=gambit_marketplace" \\
  -H "Content-Type: application/json" \\
  -H "X-Webhook-Signature: \${SIGNATURE}" \\
  -d '{
    "event": "renewal",
    "email": "customer@example.com",
    "product_code": "gambit",
    "plan_code": "pro",
    "expires_at": "2026-01-08T00:00:00Z"
  }'`}
            />

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
              3. 署名の生成方法
            </Typography>
            <CodeBlock
              label="Node.js"
              code={`const crypto = require('crypto');

const payload = JSON.stringify(data);
const signature = crypto
  .createHmac('sha256', process.env.WEBHOOK_SECRET)
  .update(payload)
  .digest('hex');

// ヘッダーに設定
headers['X-Webhook-Signature'] = signature;`}
            />

            <CodeBlock
              label="Python"
              code={`import hmac
import hashlib
import json

payload = json.dumps(data)
signature = hmac.new(
    WEBHOOK_SECRET.encode(),
    payload.encode(),
    hashlib.sha256
).hexdigest()

# ヘッダーに設定
headers['X-Webhook-Signature'] = signature`}
            />

            <Alert severity="warning" sx={{ mt: 3 }}>
              <Typography variant="subtitle2">セキュリティ注意事項</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="本番環境では必ず署名検証を有効にしてください" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="WEBHOOK_SECRETは安全に管理してください" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="HTTPSを使用してください" />
                </ListItem>
              </List>
            </Alert>
          </Box>
        </TabPanel>
      </Paper>

      {/* プロダクト設定リンク */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LinkIcon color="primary" />
            <Typography variant="h6">関連設定</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Webhookを正しく動作させるには、プロダクトの webhook_config を設定する必要があります。
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.href = '/admin/products'}
          >
            プロダクト管理画面へ
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};
